import { 
  CheckCircle, 
  Clock, 
  XCircle, 
  AlertCircle, 
  FileText, 
  Send,
  Loader2
} from "lucide-react";
import { Flag } from "@/components/Flag";
import { cn } from "@/lib/utils";

const statusConfig = {
  // Bank details status
  not_provided: {
    label: "Not Provided",
    variant: "neutral",
    icon: FileText,
  },
  pending_approval: {
    label: "Pending Approval",
    variant: "warning",
    icon: Clock,
  },
  submitted: {
    label: "Submitted",
    variant: "info",
    icon: Send,
  },
  verifying: {
    label: "Verifying",
    variant: "info",
    icon: Loader2,
  },
  verified: {
    label: "Verified",
    variant: "success",
    icon: CheckCircle,
  },
  failed: {
    label: "Failed",
    variant: "error",
    icon: XCircle,
  },
  needs_review: {
    label: "Needs Review",
    variant: "warning",
    icon: AlertCircle,
  },
  rejected: {
    label: "Rejected",
    variant: "error",
    icon: XCircle,
  },
  // DocuSign status
  not_sent: {
    label: "Not Sent",
    variant: "neutral",
    icon: FileText,
  },
  sent: {
    label: "Sent",
    variant: "info",
    icon: Send,
  },
  signed: {
    label: "Signed",
    variant: "success",
    icon: CheckCircle,
  },
  // Beneficiary status
  pending: {
    label: "Pending",
    variant: "warning",
    icon: Clock,
  },
  replaced: {
    label: "Replaced",
    variant: "neutral",
    icon: FileText,
  },
  locked: {
    label: "Locked",
    variant: "neutral",
    icon: FileText,
  },
  // Disbursement
  processing: {
    label: "Processing",
    variant: "info",
    icon: Loader2,
  },
  completed: {
    label: "Completed",
    variant: "success",
    icon: CheckCircle,
  },
};

const variantStyles = {
  success: "status-badge-success",
  warning: "status-badge-warning",
  error: "status-badge-error",
  info: "status-badge-info",
  neutral: "status-badge-neutral",
};

export function StatusBadge({ status, showIcon = true, size = "default" }) {
  const config = statusConfig[status] || {
    label: status?.replace(/_/g, " ") || "Unknown",
    variant: "neutral",
    icon: FileText,
  };

  const Icon = config.icon;

  return (
    <span
      className={cn(
        "status-badge",
        variantStyles[config.variant],
        size === "sm" && "text-[10px] px-2 py-0.5"
      )}
      data-testid={`status-badge-${status}`}
    >
      {showIcon && (
        <Icon
          className={cn(
            "mr-1",
            size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5",
            config.icon === Loader2 && "animate-spin"
          )}
        />
      )}
      {config.label}
    </span>
  );
}

export function LoanApprovedBadge() {
  return (
    <span className="status-badge status-badge-success" data-testid="loan-approved-badge">
      <CheckCircle className="w-3.5 h-3.5 mr-1" />
      Loan Approved
    </span>
  );
}

export function CountryBadge({ country }) {
  const flagCode = country === "UK" ? "GB" : country === "AU" ? "AU" : null;
  return (
    <span className="status-badge status-badge-neutral inline-flex items-center" data-testid={`country-badge-${country}`}>
      {flagCode ? <Flag code={flagCode} size={14} className="mr-1" /> : <span className="mr-1">🌐</span>}
      {country}
    </span>
  );
}

export function CurrencyBadge({ currency, amount }) {
  const symbol = currency === "GBP" ? "£" : currency === "AUD" ? "A$" : "$";
  return (
    <span className="font-mono text-lg font-semibold" data-testid="currency-amount">
      {symbol}{amount?.toLocaleString() || "0"}
    </span>
  );
}
